/**
 * Utilidades compartidas por los componentes relacionados a agendamientos
 * quirúrgicos (SurgeryCard, SurgeryDetailModal, SurgeryEditModal,
 * SurgeryTimesModal, EnCirugiaBanner). Centralizadas aquí para evitar
 * tener 5 copias del mismo código de formato de fechas.
 */

/**
 * Normaliza fechas de agendamientos a "AAAA-MM-DDTHH:MM" para evitar Invalid Date:
 *   "2026-06-17T8:30"  → "2026-06-17T08:30"   (hora sin cero)
 *   "2026-10-3T01:30"  → "2026-10-03T01:30"   (día/mes sin cero)
 *   "2026-10-32T01:00" → "2026-11-01T01:00"   (día fuera de rango, p. ej. cirugías que cruzan medianoche)
 * Si trae zona horaria u otro formato se deja como está (solo con el cero de la hora).
 */
export const normISO = (iso) => {
  if (!iso || typeof iso !== 'string') return null;
  const m = /^(\d{4})-(\d{1,2})-(\d{1,2})[T ](\d{1,2}):(\d{2})(?::(\d{2}))?$/.exec(iso.trim());
  if (!m) return iso.replace(/T(\d):/, 'T0$1:');
  const d = new Date(+m[1], +m[2] - 1, +m[3], +m[4], +m[5], +(m[6] || 0));
  if (isNaN(d)) return iso;
  const p = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}T${p(d.getHours())}:${p(d.getMinutes())}`;
};

/** "08:30" a partir de un ISO (con o sin cero inicial en la hora) */
export const fmtHora = (iso) => {
  if (!iso) return '—';
  const d = new Date(normISO(iso));
  return isNaN(d) ? '—' : d.toLocaleTimeString('es-MX', { hour: '2-digit', minute: '2-digit', hour12: false });
};

/** "martes, 17 de junio de 2026" a partir de un ISO */
export const fmtFecha = (iso) => {
  if (!iso) return '—';
  const d = new Date(normISO(iso));
  return isNaN(d) ? '—' : d.toLocaleDateString('es-MX', { weekday: 'long', day: '2-digit', month: 'long', year: 'numeric' });
};

/** "mar, 17 jun 2026" — variante corta para espacios reducidos (tarjetas) */
export const fmtFechaCorta = (iso) => {
  if (!iso) return '—';
  const d = new Date(normISO(iso));
  return isNaN(d) ? '—' : d.toLocaleDateString('es-MX', { weekday: 'short', day: '2-digit', month: 'short', year: 'numeric' });
};

/** Formatea un valor tipo datetime-local ("2026-06-17T08:30") a fecha corta + hora */
export const fmtHoraLibre = (v) => {
  if (!v) return '—';
  const d = new Date(v);
  if (isNaN(d)) return '—';
  const fecha = d.toLocaleDateString('es-MX', { day: '2-digit', month: 'short' });
  const hora  = d.toLocaleTimeString('es-MX', { hour: '2-digit', minute: '2-digit', hour12: false });
  return `${fecha} · ${hora}`;
};

/** Valor listo para <input type="datetime-local"> a partir de un ISO tipo "2026-06-17T8:30" */
export const toInputValue = (iso) => {
  const norm = normISO(iso);
  if (!norm) return '';
  const d = new Date(norm);
  return isNaN(d) ? '' : norm.slice(0, 16);
};

/** "1h 24min" o "45 min" a partir de minutos totales */
export const fmtDuracion = (min) => {
  if (min < 60) return `${min} min`;
  const h = Math.floor(min / 60);
  const m = min % 60;
  return m === 0 ? `${h}h` : `${h}h ${m}min`;
};

/** Descarta valores "vacíos" del formato de descripción del evento (NA, placeholders, etc.) */
export const val = (v) =>
  (!v || v === 'NA' || v === 'No se cuenta con la información' || v === 'No se requiere') ? null : v;

/* Extrae solo la parte descriptiva de un ítem de material/equipo,
   sin volcar el objeto completo (ignora ids, cantidades, flags, etc). */
const CAMPOS_DESCRIPTIVOS = ['nombre', 'descripcion', 'descripción', 'name', 'item', 'equipo', 'material', 'label', 'titulo', 'título'];

export const getDescriptivo = (item) => {
  if (typeof item === 'string') return item;
  if (typeof item === 'number') return String(item);
  if (item && typeof item === 'object') {
    for (const campo of CAMPOS_DESCRIPTIVOS) {
      if (val(item[campo])) return item[campo];
    }
    const primerTexto = Object.values(item).find((v) => typeof v === 'string' && val(v));
    if (primerTexto) return primerTexto;
  }
  return null;
};
